// Fetches live wind/gust/temperature observations from the Zephyr network (~500 NZ stations),
// calling the native Zephyr API shape directly (station id as a path segment, `key` query
// param) — same as any other provider, so weather-cors-proxy needs no Zephyr-specific logic,
// just its normal prefix-forward + SECRET_TOKEN substitution. Station selection is bbox-driven,
// scored by coastal exposure, elevation, proximity to the race course, and a manual overlay.

import { StationSeries, Observation } from './StationSeries.js';
import { adjustFor } from './ZephyrStationAdjust.js';
import catalog from './zephyr-stations.json';

const BASE_URL = 'https://api.zephyrapp.nz/v1/stations';

export const NZ_BBOX = { minLat: -48, maxLat: -34, minLon: 166, maxLon: 179 };
export const ZEPHYR_START_MS = Date.UTC(2026, 0, 1); // Zephyr network has no data before this
export const ELEV_WEIGHT = 6;
export const COURSE_PENALTY = 1000;
export const MAX_STATIONS = 20;
export const MIN_STATION_SCORE = 1000;
const DEG_TO_KM = 111.195;
const KMH_TO_KNOTS = 1.852;

export class ZephyrProvider {

  constructor(base = BASE_URL, token = 'SECRET_TOKEN', fetchFn = fetch.bind(globalThis), stations = catalog) {
    this.base = base;
    this.token = token;
    this.fetchFn = fetchFn;
    this.stations = stations;
  }

  // Returns one StationSeries per catalog station in the bbox — not just the ones that made
  // the relevance cut. Stations that made the cut (selectStations) get real observations;
  // the rest (and any that made the cut but failed/returned no data) get an empty StationSeries,
  // so the UI can still place a pin for every candidate station in view.
  // forceFetch bypasses the relevance cut (MIN_STATION_SCORE/MAX_STATIONS) and fetches every
  // in-bbox station — used for on-demand single-station fetches (spec 25 N3), where the bbox is
  // already tiny and the user explicitly asked for this exact station regardless of its relevance score.
  async fetch(start, end, bbox, now = Date.now(), coursePoints = [], forceFetch = false) {
    if (end < ZEPHYR_START_MS) return [];

    if (!bboxOverlapsNZ(bbox)) {
      throw new Error('ZephyrProvider: bbox does not overlap New Zealand');
    }

    const inBox = stationsInBox(this.stations, bbox);
    const selected = forceFetch ? inBox : selectStations(this.stations, bbox, coursePoints);
    const results = await Promise.allSettled(selected.map(s => this.fetchStation(s, start, end)));

    const dataById = new Map();
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value) {
        dataById.set(selected[i].id, r.value);
      } else if (r.status === 'rejected') {
        console.warn(`ZephyrProvider: station ${selected[i].id} failed:`, r.reason);
      }
    });

    // score every in-bbox station (not just the fetched subset) so map declutter (spec 25)
    // can rank every candidate pin, not only the ones that made the relevance cut
    const courseRange = courseRangeOf(coursePoints);
    return inBox.map(station => {
      const score = scoreStation(station, coursePoints, courseRange);
      const series = dataById.get(station.id) ?? new StationSeries(station.id, station.name, station.lat, station.lon, 'zephyr');
      series.score = score;
      return series;
    });
  }

  async fetchStation(station, start, end) {
    const { dateFrom, dateTo } = toDateRange(start, end);
    const params = new URLSearchParams({ dateFrom, dateTo, key: this.token });
    const url = `${this.base}/${station.id}/data?${params}`;
    const res = await this.fetchFn(url);
    if (!res.ok) throw new Error(`ZephyrProvider: HTTP ${res.status} for station ${station.id}`);
    const samples = await res.json();

    const series = new StationSeries(station.id, station.name, station.lat, station.lon, 'zephyr');
    for (const sample of samples) {
      const t = sample.time * 1000;
      if (t < start || t > end) continue;
      const ff = sample.windAverage != null ? sample.windAverage / KMH_TO_KNOTS : null;
      const gust = sample.windGust != null ? sample.windGust / KMH_TO_KNOTS : null;
      series.addObservation(new Observation(t, sample.windBearing, ff, sample.temperature, null, gust));
    }
    return series.observations.length ? series : null;
  }
}

export function bboxOverlapsNZ(bbox) {
  return bbox.minLat <= NZ_BBOX.maxLat && bbox.maxLat >= NZ_BBOX.minLat &&
         bbox.minLon <= NZ_BBOX.maxLon && bbox.maxLon >= NZ_BBOX.minLon;
}

export function stationsInBox(stations, bbox) {
  return stations.filter(s => s.lat >= bbox.minLat && s.lat <= bbox.maxLat && s.lon >= bbox.minLon && s.lon <= bbox.maxLon);
}

// Relevance selection (spec 24, D4): score = coastDistM - ELEV_WEIGHT*elevation
// + coursePenalty(courseDist) + adjustM, ascending, capped at MAX_STATIONS.
// COAST_M is infinite per the finalised mockup tuning (M4) — MAX_STATIONS is the only cap.
export function selectStations(stations, bbox, coursePoints = []) {
  const courseRange = courseRangeOf(coursePoints);
  return stationsInBox(stations, bbox)
    .map(station => ({ station, score: scoreStation(station, coursePoints, courseRange) }))
    .sort((a, b) => a.score - b.score)
    .filter((s) => s.score < MIN_STATION_SCORE)
    .slice(0, MAX_STATIONS)
    .map(x => x.station);
}

export function scoreStation(station, coursePoints, courseRange) {
  const courseDist = courseDistOf(station.lon, station.lat, coursePoints);
  const penalty = isFinite(courseDist) ? COURSE_PENALTY * Math.min(courseDist / courseRange, 1) : 0;
  return station.coastDistM - ELEV_WEIGHT * station.elevation + penalty + adjustFor(station.id);
}

function courseDistOf(lon, lat, coursePoints) {
  let min = Infinity;
  for (const c of coursePoints) {
    const mLon = 111320 * Math.cos((lat + c.lat) / 2 * Math.PI / 180);
    const d = Math.hypot((lon - c.lon) * mLon, (lat - c.lat) * 111132);
    if (d < min) min = d;
  }
  return min;
}

// COURSE_RANGE = max(widthKm, heightKm) / 2 of the course points' own bounding box (spec 24, D4).
function courseRangeOf(coursePoints) {
  if (coursePoints.length < 2) return 1;
  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  for (const c of coursePoints) {
    if (c.lat < minLat) minLat = c.lat;
    if (c.lat > maxLat) maxLat = c.lat;
    if (c.lon < minLon) minLon = c.lon;
    if (c.lon > maxLon) maxLon = c.lon;
  }
  const midLat = (minLat + maxLat) / 2;
  const heightKm = (maxLat - minLat) * DEG_TO_KM;
  const widthKm = (maxLon - minLon) * DEG_TO_KM * Math.cos(midLat * Math.PI / 180);
  return Math.max(Math.max(widthKm, heightKm) * 1000 / 2, 1);
}

// dateFrom inclusive, dateTo exclusive (spec 24, N4/N8)
function toDateRange(start, end) {
  const dateFrom = new Date(start).toISOString().slice(0, 10);
  const endPlusOne = new Date(end);
  endPlusOne.setUTCDate(endPlusOne.getUTCDate() + 1);
  const dateTo = endPlusOne.toISOString().slice(0, 10);
  return { dateFrom, dateTo };
}
